
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/hooks/useSession";
import { useCallback } from "react";

export function useResourceShareUpdater(resourceId: string) {
  const { session } = useSession();

  const updateResourceShareWithProgress = useCallback(async (calculatedProgress: number): Promise<void> => {
    if (!session?.user?.id) return;
    
    try {
      interface ResourceShareUpdate {
        resource_id: string;
        trainee_id: string;
        progress: number;
        status: string;
        completed_at?: string;
      }
      
      const { data: existingShare, error: shareCheckError } = await supabase
        .from('resource_shares')
        .select('id, status, progress, completed_at')
        .eq('resource_id', resourceId)
        .eq('trainee_id', session.user.id)
        .maybeSingle();
        
      if (shareCheckError) {
        console.error('Error checking for existing resource_shares:', shareCheckError);
        return;
      }
      
      console.log('Existing share data:', existingShare);
      
      const updateData: ResourceShareUpdate = {
        resource_id: resourceId,
        trainee_id: session.user.id,
        progress: calculatedProgress,
        status: existingShare?.status || 'accepted'
      };
      
      // Only set completed_at if we've reached 100% and it's not already set
      if (calculatedProgress === 100 && (!existingShare || !existingShare.completed_at)) {
        const now = new Date().toISOString();
        updateData.completed_at = now;
      }
      
      if (existingShare?.id) {
        // Only update if our calculated progress differs or is higher
        if (existingShare.progress !== calculatedProgress && 
            (calculatedProgress > (existingShare.progress || 0) || calculatedProgress === 100)) {
          console.log(`Updating resource_shares progress from ${existingShare.progress || 0}% to ${calculatedProgress}%`);
          
          const { error: updateError } = await supabase
            .from('resource_shares')
            .update(updateData)
            .eq('id', existingShare.id);
            
          if (updateError) {
            console.error('Error updating resource_shares:', updateError);
          } else {
            console.log(`Updated resource_shares with progress: ${calculatedProgress}%`);
          }
        }
      } else {
        console.log(`Creating new resource_shares with progress: ${calculatedProgress}%`);
        
        const { error: insertError } = await supabase
          .from('resource_shares')
          .insert(updateData);
          
        if (insertError) {
          console.error('Error inserting resource_shares:', insertError);
        } else {
          console.log(`Created resource_shares with progress: ${calculatedProgress}%`);
        }
      }
    } catch (shareError) {
      console.error('Error updating resource shares:', shareError);
    }
  }, [resourceId, session?.user?.id]);

  return { updateResourceShareWithProgress };
}
